import React, { useContext, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import AutoFixHighOutlinedIcon from '@mui/icons-material/AutoFixHighOutlined';
import { useSelector } from 'react-redux';
import { FirebaseContext } from 'common';

const IDENTICAL_ALLOWLIST = new Set([
  'ok',
  'airport_queue_plukk',
  'airport_queue_uber',
  'airport_queue_live',
  'airport_queue_min',
  'live',
]);

function normalizeLangList(langlist) {
  if (Array.isArray(langlist)) return langlist.filter(Boolean);
  if (langlist && typeof langlist === 'object') return Object.values(langlist).filter(Boolean);
  return [];
}

function downloadJson(filename, data) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default function LanguageSettings() {
  const firebase = useContext(FirebaseContext);
  const languagedata = useSelector((state) => state.languagedata);

  const languages = useMemo(() => {
    const list = normalizeLangList(languagedata?.langlist);
    return [...list].sort((a, b) => String(a.langName || '').localeCompare(String(b.langName || '')));
  }, [languagedata?.langlist]);

  const [selectedCode, setSelectedCode] = useState('en');
  const [filter, setFilter] = useState('all'); // all | missing | identical
  const [query, setQuery] = useState('');
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  const [newLang, setNewLang] = useState({ code: '', name: '', dateLocale: 'en', rtl: false });
  const [newKey, setNewKey] = useState({ key: '', enValue: '' });
  const [busyAddKey, setBusyAddKey] = useState(false);
  const [busyAddLang, setBusyAddLang] = useState(false);
  const [aiStatus, setAiStatus] = useState({ loading: false, configured: false, lastTestAt: null, lastTestOk: false, lastError: null });
  const [aiBusy, setAiBusy] = useState(false);
  const [aiMode, setAiMode] = useState('identical'); // identical | missing | all
  const [aiMaxKeys, setAiMaxKeys] = useState(50);

  const langByCode = useMemo(() => {
    const map = new Map();
    for (const item of languages) {
      const code = item?.langLocale || item?.id;
      if (code) map.set(code, item);
    }
    return map;
  }, [languages]);

  const enLang = langByCode.get('en');
  const enMap = useMemo(() => {
    return (enLang?.keyValuePairs && typeof enLang.keyValuePairs === 'object' ? enLang.keyValuePairs : {}) || {};
  }, [enLang]);

  const currentLang = langByCode.get(selectedCode);
  const currentCode = currentLang?.langLocale || selectedCode;
  const currentMap = useMemo(() => {
    return (
      (currentLang?.keyValuePairs && typeof currentLang.keyValuePairs === 'object' ? currentLang.keyValuePairs : {}) ||
      {}
    );
  }, [currentLang]);

  const keys = useMemo(() => {
    const baseKeys = Object.keys(enMap || {});
    baseKeys.sort();
    const q = query.trim().toLowerCase();

    return baseKeys.filter((k) => {
      const enVal = String(enMap[k] ?? '');
      const val = String(draft[k] ?? currentMap[k] ?? '');
      const missing = val.trim() === '';
      const identical =
        currentCode !== 'en' && !missing && !IDENTICAL_ALLOWLIST.has(k) && val === enVal && enVal.trim() !== '';

      if (filter === 'missing' && !missing) return false;
      if (filter === 'identical' && !identical) return false;
      if (!q) return true;
      return (
        k.toLowerCase().includes(q) ||
        enVal.toLowerCase().includes(q) ||
        val.toLowerCase().includes(q)
      );
    });
  }, [currentCode, currentMap, draft, enMap, filter, query]);

  const stats = useMemo(() => {
    let missing = 0;
    let identical = 0;
    const allKeys = Object.keys(enMap || {});
    for (const k of allKeys) {
      const enVal = String(enMap[k] ?? '');
      const val = String(draft[k] ?? currentMap[k] ?? '');
      const isMissing = val.trim() === '';
      if (isMissing) {
        missing++;
        continue;
      }
      if (currentCode !== 'en' && !IDENTICAL_ALLOWLIST.has(k) && val === enVal && enVal.trim() !== '') {
        identical++;
      }
    }
    return { total: allKeys.length, missing, identical };
  }, [currentCode, currentMap, draft, enMap]);

  const hasDraftChanges = useMemo(() => Object.keys(draft).length > 0, [draft]);

  const getIdToken = async () => {
    const user = firebase?.auth?.currentUser;
    if (!user) return null;
    return user.getIdToken();
  };

  const apiFetch = async (url, options = {}) => {
    const token = await getIdToken();
    const headers = {
      'Content-Type': 'application/json',
      ...(options.headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
    const res = await fetch(url, { ...options, headers });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const message = data?.error || `Request failed (${res.status})`;
      throw new Error(message);
    }
    return data;
  };

  const refreshAiStatus = async () => {
    setAiStatus((s) => ({ ...s, loading: true }));
    try {
      const data = await apiFetch('/api/i18n-ai/status', { method: 'GET' });
      setAiStatus({ loading: false, ...data });
    } catch (e) {
      setAiStatus((s) => ({ ...s, loading: false, lastError: e.message }));
    }
  };

  const aiTest = async () => {
    setAiBusy(true);
    try {
      await apiFetch('/api/i18n-ai/test', { method: 'POST', body: JSON.stringify({}) });
      await refreshAiStatus();
    } finally {
      setAiBusy(false);
    }
  };

  const aiSuggest = async () => {
    if (!currentCode || currentCode === 'en') return;
    setAiBusy(true);
    try {
      await apiFetch('/api/i18n-ai/suggest', {
        method: 'POST',
        body: JSON.stringify({ lang: currentCode, mode: aiMode, maxKeys: aiMaxKeys }),
      });
    } finally {
      setAiBusy(false);
    }
  };

  const loadAiSuggestionsToDraft = async () => {
    if (!currentCode || currentCode === 'en') return;
    setAiBusy(true);
    try {
      const data = await apiFetch(`/api/i18n-ai/suggestions?lang=${encodeURIComponent(currentCode)}`, { method: 'GET' });
      const suggestions = data?.suggestions || {};
      const nextDraft = {};
      for (const [k, v] of Object.entries(suggestions)) {
        if (v && typeof v.value === 'string') nextDraft[k] = v.value;
      }
      setDraft((prev) => ({ ...prev, ...nextDraft }));
    } finally {
      setAiBusy(false);
    }
  };

  const clearAiSuggestions = async () => {
    if (!currentCode || currentCode === 'en') return;
    setAiBusy(true);
    try {
      await apiFetch('/api/i18n-ai/clear', { method: 'POST', body: JSON.stringify({ lang: currentCode }) });
    } finally {
      setAiBusy(false);
    }
  };

  const saveChanges = async () => {
    if (!firebase?.database?.ref) return;
    if (!currentCode) return;
    if (!hasDraftChanges) return;

    const updates = {};
    for (const [k, v] of Object.entries(draft)) {
      updates[`languages/${currentCode}/keyValuePairs/${k}`] = v;
    }

    setSaving(true);
    try {
      await firebase.database.ref().update(updates);
      setDraft({});
    } finally {
      setSaving(false);
    }
  };

  const fillMissingFromEnglish = async () => {
    if (!firebase?.database?.ref) return;
    if (!currentCode || currentCode === 'en') return;
    const updates = {};
    for (const k of Object.keys(enMap || {})) {
      const val = String(draft[k] ?? currentMap[k] ?? '');
      if (val.trim() !== '') continue;
      updates[`languages/${currentCode}/keyValuePairs/${k}`] = String(enMap[k] ?? '');
    }
    if (!Object.keys(updates).length) return;

    setSaving(true);
    try {
      await firebase.database.ref().update(updates);
      setDraft({});
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = () => {
    const merged = { ...currentMap, ...draft };
    downloadJson(`translations-${currentCode}.json`, merged);
  };

  const handleAddLanguage = async () => {
    if (!firebase?.languagesEditRef || !firebase?.database?.ref) return;
    const code = newLang.code.trim();
    const name = newLang.name.trim();
    const dateLocale = (newLang.dateLocale || 'en').trim();

    if (!/^[a-z]{2,3}(-[A-Z]{2})?$/.test(code)) return;
    if (!name) return;
    if (langByCode.has(code)) return;
    if (!Object.keys(enMap || {}).length) return;

    setBusyAddLang(true);
    try {
      await firebase.languagesEditRef(code).set({
        langName: name,
        langLocale: code,
        dateLocale,
        rtl: Boolean(newLang.rtl),
        default: false,
        keyValuePairs: { ...enMap },
      });
      setNewLang({ code: '', name: '', dateLocale: 'en', rtl: false });
      setSelectedCode(code);
      setDraft({});
    } finally {
      setBusyAddLang(false);
    }
  };

  const handleAddKey = async () => {
    if (!firebase?.database?.ref) return;
    const key = newKey.key.trim();
    const enValue = newKey.enValue;
    if (!/^[A-Za-z_][A-Za-z0-9_]*$/.test(key)) return;
    if (!enValue.trim()) return;

    setBusyAddKey(true);
    try {
      const updates = {};
      const codes = Array.from(langByCode.keys());
      for (const code of codes) {
        updates[`languages/${code}/keyValuePairs/${key}`] = enValue;
      }
      await firebase.database.ref().update(updates);
      setNewKey({ key: '', enValue: '' });
    } finally {
      setBusyAddKey(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', px: 2, py: 2 }}>
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 800 }}>
          Language settings
        </Typography>
        <Chip size="small" label={`Keys: ${stats.total}`} />
        <Chip size="small" color={stats.missing ? 'warning' : 'default'} label={`Missing: ${stats.missing}`} />
        <Chip
          size="small"
          color={stats.identical ? 'info' : 'default'}
          label={`Same as EN: ${stats.identical}`}
        />
        <Box sx={{ flex: 1 }} />
        <Button
          variant="outlined"
          size="small"
          startIcon={<DownloadOutlinedIcon />}
          onClick={handleDownload}
          disabled={!currentCode}
        >
          Download JSON
        </Button>
        <Button
          variant="contained"
          size="small"
          startIcon={<SaveOutlinedIcon />}
          onClick={saveChanges}
          disabled={!hasDraftChanges || saving}
        >
          Save changes
        </Button>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '340px 1fr' }, gap: 2 }}>
        <Paper sx={{ p: 2, borderRadius: 3 }}>
          <Typography sx={{ fontWeight: 700, mb: 1 }}>Languages</Typography>
          <FormControl fullWidth size="small">
            <InputLabel id="lang-select-label">Language</InputLabel>
            <Select
              labelId="lang-select-label"
              label="Language"
              value={selectedCode}
              onChange={(e) => {
                setSelectedCode(String(e.target.value));
                setDraft({});
              }}
            >
              {Array.from(langByCode.keys()).sort().map((code) => (
                <MenuItem key={code} value={code}>
                  {langByCode.get(code)?.langName || code}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Divider sx={{ my: 2 }} />

          <Typography sx={{ fontWeight: 700, mb: 1 }}>Filters</Typography>
          <FormControl fullWidth size="small" sx={{ mb: 1 }}>
            <InputLabel id="filter-select-label">Show</InputLabel>
            <Select
              labelId="filter-select-label"
              label="Show"
              value={filter}
              onChange={(e) => setFilter(String(e.target.value))}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="missing">Missing only</MenuItem>
              <MenuItem value="identical">Same as English</MenuItem>
            </Select>
          </FormControl>
          <TextField
            size="small"
            fullWidth
            label="Search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />

          <Divider sx={{ my: 2 }} />

          <Typography sx={{ fontWeight: 700, mb: 1 }}>Actions</Typography>
          <Button
            size="small"
            variant="outlined"
            startIcon={<ContentCopyOutlinedIcon />}
            onClick={fillMissingFromEnglish}
            disabled={saving || currentCode === 'en'}
            fullWidth
            sx={{ mb: 1 }}
          >
            Fill missing with English
          </Button>

          <Divider sx={{ my: 2 }} />

          <Typography sx={{ fontWeight: 700, mb: 1 }}>AI assistant</Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
            <Chip
              size="small"
              color={aiStatus.configured ? 'success' : 'default'}
              label={aiStatus.configured ? 'OpenAI: configured' : 'OpenAI: not configured'}
            />
            {aiStatus.lastTestAt ? (
              <Chip
                size="small"
                color={aiStatus.lastTestOk ? 'success' : 'warning'}
                label={`Last test: ${aiStatus.lastTestOk ? 'OK' : 'Failed'}`}
              />
            ) : null}
          </Box>

          <Box className="row" sx={{ gap: 1, mb: 1 }}>
            <Button size="small" variant="outlined" onClick={refreshAiStatus} disabled={aiStatus.loading || aiBusy}>
              Refresh
            </Button>
            <Button size="small" variant="outlined" onClick={aiTest} disabled={aiBusy}>
              Test OpenAI
            </Button>
          </Box>

          <FormControl fullWidth size="small" sx={{ mb: 1 }}>
            <InputLabel id="ai-mode-label">AI mode</InputLabel>
            <Select
              labelId="ai-mode-label"
              label="AI mode"
              value={aiMode}
              onChange={(e) => setAiMode(String(e.target.value))}
            >
              <MenuItem value="identical">Translate “same as English”</MenuItem>
              <MenuItem value="missing">Fill missing only</MenuItem>
              <MenuItem value="all">Fix missing + identical + placeholders</MenuItem>
            </Select>
          </FormControl>

          <TextField
            size="small"
            label="Max keys per run"
            type="number"
            value={aiMaxKeys}
            onChange={(e) => setAiMaxKeys(Number(e.target.value))}
            fullWidth
            sx={{ mb: 1 }}
            inputProps={{ min: 1, max: 200 }}
          />

          <Button
            size="small"
            variant="contained"
            startIcon={<AutoFixHighOutlinedIcon />}
            onClick={aiSuggest}
            disabled={aiBusy || currentCode === 'en' || !aiStatus.configured}
            fullWidth
            sx={{ mb: 1 }}
          >
            Generate suggestions
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={loadAiSuggestionsToDraft}
            disabled={aiBusy || currentCode === 'en'}
            fullWidth
            sx={{ mb: 1 }}
          >
            Load suggestions into editor
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={clearAiSuggestions}
            disabled={aiBusy || currentCode === 'en'}
            fullWidth
          >
            Clear saved suggestions
          </Button>

          {aiStatus.lastError ? (
            <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#b91c1c' }}>
              {aiStatus.lastError}
            </Typography>
          ) : null}

          <Divider sx={{ my: 2 }} />

          <Typography sx={{ fontWeight: 700, mb: 1 }}>Add language</Typography>
          <TextField
            size="small"
            label="Code (e.g. fr, de, pt-BR)"
            value={newLang.code}
            onChange={(e) => setNewLang((p) => ({ ...p, code: e.target.value }))}
            fullWidth
            sx={{ mb: 1 }}
          />
          <TextField
            size="small"
            label="Name"
            value={newLang.name}
            onChange={(e) => setNewLang((p) => ({ ...p, name: e.target.value }))}
            fullWidth
            sx={{ mb: 1 }}
          />
          <TextField
            size="small"
            label="Date locale"
            value={newLang.dateLocale}
            onChange={(e) => setNewLang((p) => ({ ...p, dateLocale: e.target.value }))}
            fullWidth
            sx={{ mb: 1 }}
          />
          <Button
            size="small"
            variant="contained"
            startIcon={<AddOutlinedIcon />}
            onClick={handleAddLanguage}
            disabled={busyAddLang}
            fullWidth
          >
            Add language
          </Button>

          <Divider sx={{ my: 2 }} />

          <Typography sx={{ fontWeight: 700, mb: 1 }}>Add key</Typography>
          <TextField
            size="small"
            label="Key (snake_case)"
            value={newKey.key}
            onChange={(e) => setNewKey((p) => ({ ...p, key: e.target.value }))}
            fullWidth
            sx={{ mb: 1 }}
          />
          <TextField
            size="small"
            label="English value"
            value={newKey.enValue}
            onChange={(e) => setNewKey((p) => ({ ...p, enValue: e.target.value }))}
            fullWidth
            sx={{ mb: 1 }}
          />
          <Button
            size="small"
            variant="contained"
            startIcon={<AddOutlinedIcon />}
            onClick={handleAddKey}
            disabled={busyAddKey}
            fullWidth
          >
            Add key to all languages
          </Button>
        </Paper>

        <Paper sx={{ p: 2, borderRadius: 3 }}>
          <Typography sx={{ fontWeight: 800, mb: 1 }}>
            Editor {currentCode ? `(${currentCode})` : ''}
          </Typography>
          <Typography variant="body2" sx={{ color: '#6B7280', mb: 2 }}>
            Missing values are empty. “Same as English” helps spot untranslated strings.
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 1.5 }}>
            {keys.map((k) => {
              const enVal = String(enMap[k] ?? '');
              const val = String(draft[k] ?? currentMap[k] ?? '');
              const missing = val.trim() === '';
              const identical =
                currentCode !== 'en' && !missing && !IDENTICAL_ALLOWLIST.has(k) && val === enVal && enVal.trim() !== '';

              return (
                <Paper key={k} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Typography sx={{ fontFamily: 'monospace', fontSize: 12 }}>{k}</Typography>
                    {missing ? (
                      <Chip size="small" color="warning" label="Missing" />
                    ) : identical ? (
                      <Chip size="small" color="info" label="Same as EN" />
                    ) : (
                      <Chip size="small" label="OK" />
                    )}
                  </Box>
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" sx={{ color: '#6B7280' }}>
                      English
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {enVal}
                    </Typography>
                  </Box>
                  <Box sx={{ mt: 1 }}>
                    <TextField
                      size="small"
                      fullWidth
                      label="Translation"
                      value={val}
                      onChange={(e) => setDraft((p) => ({ ...p, [k]: e.target.value }))}
                    />
                  </Box>
                </Paper>
              );
            })}

            {!keys.length ? (
              <Typography variant="body2" sx={{ color: '#6B7280' }}>
                No keys match the current filter.
              </Typography>
            ) : null}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}
